import { mockShell } from "./shell.mock"
import { createBridgeShell } from "./bridge"

export type ShellLocale = "fa-IR" | "en"
export type ShellTheme = "daylight" | "midnight"
export type MatchMode = "voice" | "video"
export type IntentLang = "fa" | "en" | "mixed"
export type ModerationCategory = "harassment" | "sexual" | "violence" | "scam" | "misinfo" | "other"
export type ReportHistoryStatus = "reviewing" | "valid" | "invalid"

export interface JoowIdentity {
  getPrincipal(): Promise<{ principalToken: string; region: "IR" | string; locale: ShellLocale }>
  isAuthenticated(): Promise<boolean>
  requireAuth(): Promise<void>
}

export interface MatchHandle {
  onMatched(cb: (match: {
    sessionId: string
    peerToken: string
    topicLabel: string
    matchScore: number
  }) => void): () => void
  next(): Promise<void>
  leave(): Promise<void>
  onlineCount(cb: (count: number) => void): () => void
}

export interface JoowMatch {
  embedIntent(input: { text: string | null; lang: IntentLang }): Promise<{ vector: number[]; normalizedTopic?: string }>
  enqueue(req: { vector: number[] | null; mode: MatchMode; topicLabel?: string }): Promise<MatchHandle>
}

export interface MediaSession {
  localStream: MediaStream
  remoteStream: MediaStream
  setMicEnabled(on: boolean): void
  setCameraEnabled(on: boolean): void
  onPeerAudioLevel(cb: (level: number) => void): () => void
  onState(cb: (state: "connecting" | "live" | "reconnecting" | "ended") => void): () => void
  stop(): void
}

export interface JoowMedia {
  start(opts: { sessionId: string; mode: MatchMode; requireCamera?: boolean }): Promise<MediaSession>
}

export interface ModerationReportInput {
  sessionId: string
  peerToken: string
  category: ModerationCategory
  note?: string
  block?: boolean
}

export interface ReportHistoryItem {
  reportId: string
  category: string
  createdAt: string
  status: ReportHistoryStatus
  outcome?: string
}

export interface JoowModeration {
  report(report: ModerationReportInput): Promise<{ reportId: string; status: "received" }>
  myReports(): Promise<ReportHistoryItem[]>
  block(peerToken: string): Promise<void>
}

export interface JoowSurface {
  theme(): ShellTheme
  onThemeChange(cb: (theme: ShellTheme) => void): () => void
  onLocaleChange?: (cb: (locale: ShellLocale) => void) => () => void
  locale(): ShellLocale
  setTitle(title: string): void
  onForeground(cb: () => void): () => void
  haptics(kind: "light" | "success" | "warning"): void
}

export interface JoowShell {
  identity: JoowIdentity
  match: JoowMatch
  media: JoowMedia
  moderation: JoowModeration
  surface: JoowSurface
  version: string
}

declare global {
  interface Window {
    joow?: JoowShell
    Joow?: JoowShell
    __JOOW_TEST_CONTEXT__?: { locale?: "fa" | "en"; theme?: "light" | "dark" }
  }
}

export const joow: JoowShell = window.joow ?? window.Joow ?? createBridgeShell() ?? mockShell
